<template>
	<base-modal
		:showCloseButton="true"
		:opened="isOpen"
		:maxWidth="'7xl'"
		@close="handleDecline"
	>
		<template #body>
			<div class="p-4 space-y-6">
				<!-- Header -->
				<div class="text-center space-y-3">
					<span
						class="inline-block bg-blue-50 text-blue-600 text-sm font-medium rounded-full px-4 py-1"
					>
						We have options for you
					</span>
					<h2
						id="modal-headline"
						class="text-2xl md:text-3xl font-bold leading-tight"
					>
						Before you go...
					</h2>
					<p class="text-gray-600">
						You can continue learning with a plan that better fits
						your needs.
					</p>
				</div>

				<!-- Plan Cards -->
				<div class="grid grid-cols-1 md:grid-cols-3 gap-6">
					<div
						v-for="plan in plans"
						:key="plan.detail.key"
						class="rounded-lg shadow-lg overflow-hidden flex flex-col"
					>
						<!-- Current plan banner or colored top bar -->
						<div
							v-if="plan.isCurrent"
							class="bg-gray-700 text-white text-center text-sm font-semibold py-2"
						>
							Your Current Plan
						</div>
						<div v-else class="h-2 w-full" :class="plan.colors.topBar"></div>

						<!-- Card body -->
						<div class="bg-white p-6 flex flex-col flex-grow">
							<h3 class="text-2xl font-bold leading-tight">
								{{ plan.detail.name }}
							</h3>
							<div class="mt-4 flex items-baseline gap-1">
								<span class="text-4xl font-bold text-gray-900">
									${{ plan.detail.price["1y"] }}
								</span>
								<span class="text-gray-500">
									/every 12 months
								</span>
							</div>
							<p class="text-gray-500 text-sm mt-2">
								{{ plan.detail.totalText["1y"] }}
							</p>
							<div v-if="!plan.isCurrent" class="mt-auto pt-6">
								<button
									type="button"
									class="button rounded-md border-none px-6 py-3 text-base font-semibold w-full"
									:class="plan.colors.button"
									@click="switchToPlan(plan.detail.key)"
								>
									Switch to this plan
								</button>
							</div>
						</div>

						<!-- Features section -->
						<div class="p-6" :class="plan.colors.featuresBg">
							<h4 class="font-bold text-lg mb-3">
								Plan Features
							</h4>
							<ul class="space-y-2">
								<li
									v-for="feature in plan.basicFeatures"
									:key="feature.description"
									class="flex items-start gap-2"
								>
									<CheckCircleIcon
										class="h-5 w-5 text-green-600 shrink-0 mt-0.5"
									/>
									<span class="text-gray-700 text-sm">
										{{ feature.description }}
									</span>
								</li>
							</ul>
						</div>
					</div>
				</div>

				<!-- Decline link -->
				<div class="text-center pt-2">
					<button
						type="button"
						class="text-gray-600 hover:text-gray-800 text-sm no-underline hover:underline"
						@click="handleDecline"
					>
						No thanks, cancel my subscription
					</button>
				</div>
			</div>
		</template>
	</base-modal>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useStore } from "vuex";
import { analytics } from "@/services/segment.ts";
import BaseModal from "./BaseModal.vue";
import { useSwitchPlanModalStore } from "@/stores/switchPlanModalStore";
import { CheckCircleIcon } from "@heroicons/vue/24/solid";
import { planDetailsMapping } from "@/components/Account/Checkout/Pricing/features.ts";
import type { PlanKeys } from "@/components/Account/Checkout/types";

const emit = defineEmits<{
	(e: "decline"): void;
}>();

interface PlanColors {
	topBar: string;
	button: string;
	featuresBg: string;
}

const colorsByPlan: Record<string, PlanColors> = {
	student: {
		topBar: "bg-blue-600",
		button: "bg-blue-600 hover:bg-blue-700 text-white",
		featuresBg: "bg-blue-50",
	},
	professional: {
		topBar: "bg-gray-700",
		button: "bg-gray-700 hover:bg-gray-600 text-white",
		featuresBg: "bg-gray-50",
	},
	ce: {
		topBar: "bg-purple-600",
		button: "bg-purple-600 hover:bg-purple-700 text-white",
		featuresBg: "bg-purple-100",
	},
	ardms: {
		topBar: "bg-teal-600",
		button: "bg-teal-600 hover:bg-teal-700 text-white",
		featuresBg: "bg-teal-50",
	},
};

const store = useStore();
const currentPlan = computed<PlanKeys>(() => store.state.user?.subscription_plan);
// const currentFrequency = computed(() => store.state.user?.subscription_frequency);

const plans = computed(() =>
	Object.values(planDetailsMapping).map((detail) => ({
		detail: detail!,
		colors: colorsByPlan[detail!.key],
		basicFeatures: detail!.features.filter((f) => f.type === "basic"),
		isCurrent: detail!.key === currentPlan.value,
	}))
);

const modalStore = useSwitchPlanModalStore();
const isOpen = computed(() => modalStore.isOpen);

const switchToPlan = (planKey: PlanKeys) => {
	analytics.track("D2C Cancel Save Offer Accepted", { variant: "switch_plan" });
	window.location.href = `/account/checkout/change-plan?from=${currentPlan.value}&to=${planKey}&frequency=1y`;
};

const handleDecline = () => {
	analytics.track("D2C Cancel Save Offer Declined", { variant: "switch_plan" });
	modalStore.close();
	emit("decline");
};

watch(isOpen, (val) => {
	if (val) analytics.track("D2C Cancel Save Offer Presented", { variant: "switch_plan" });
});
</script>
